import { and, gt, not } from 'ramda';
import React, { FC, ReactNode, useState } from 'react';
import { animated, useSpring } from 'react-spring';
import styled from 'styled-components';

import useDivWidth from '@hooks/useDivWidth';

import { exists } from '@utils/general';

import theme from '@theme/FolxTheme';

interface ICardDrawerProps {
	children: ReactNode;
	collapsedContent: ReactNode;
	expanded: boolean;
	className?: string;
	handleAnimating?: (animating: boolean) => void;
	shouldAnimate?: boolean;
}

const Wrapper = styled.div`
	width: 100%;
	overflow: hidden;
`;

const AnimatedPanel = animated(styled.div<{ width?: number }>`
	display: flex;
	gap: ${theme.spacing.sm};
	> * {
		height: fit-content;
		min-width: ${({ width }) => `${width}px`};
	}
`);

const CardDrawer: FC<ICardDrawerProps> = ({
	children,
	className,
	collapsedContent,
	expanded,
	handleAnimating = () => {
		return;
	},
	shouldAnimate = true,
}) => {
	const [firstRender, setFirstRender] = useState(true);
	const [animating, setAnimating] = useState(false);
	const { ref, width } = useDivWidth();

	const animation = useSpring({
		from: {
			width: animating || expanded ? width * 2 + 16 : width,
			x: expanded ? -(width + 16) : 0,
		},
		to: {
			x: expanded ? -(width + 16) : 0,
		},
		onStart: () => {
			setAnimating(true);
			handleAnimating(true);
		},
		onRest: () => {
			handleAnimating(false);
			setAnimating(false);
			setFirstRender(false);
		},
		config: { mass: 1, tension: 200, friction: 28 },
		immediate: firstRender || not(shouldAnimate),
	});

	return (
		<Wrapper ref={ref} className={className}>
			{shouldAnimate ? (
				and(exists(width), gt(width, 0)) && (
					<AnimatedPanel style={animation} width={width}>
						{collapsedContent}
						{(expanded || animating) && children}
					</AnimatedPanel>
				)
			) : (
				<AnimatedPanel style={animation} width={width}>
					{collapsedContent}
					{expanded && children}
				</AnimatedPanel>
			)}
		</Wrapper>
	);
};

export default styled(CardDrawer)``;
